import type { AxiosError } from 'axios';
import { makeAutoObservable } from 'mobx';

import { mapApiToDeceased } from 'src/adapters';
import { doGet } from 'src/api';
import {
  ApiResponseStatus,
  ApiRoute,
  CNAME,
  CONFIG_REQUIRED_PARAMS_MAPPED,
  ErrorText,
  NO_SLUG,
  THEME_REQUIRED_PROPERTY,
} from 'src/constants';
import {
  filterEmptyObjectProperties,
  getDeceasedYearsLife,
  getGraveLocation,
  getPersonName,
  redirectToMain,
} from 'src/utils';
import type { IDeceased, IOption, IPerson } from 'src/interfaces';

interface ICemeteryRecord {
  id: string;
  name: string;
}

class GlobalStore {
  init() {
    if (!this.isConfigCorrect) {
      return;
    }

    let slug;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      slug = urlParams.get('slug') || process.env.REACT_APP_SLUG;
      this.cartString = urlParams.get('cart') || '';
    } catch (e) {
      console.log(e);
    }

    if (slug) {
      return this.loadDeceasedData(slug).then((deceased) => {
        if (deceased) {
          this.setSlug(deceased.slug);
          this.setCemeteryName(deceased.cemetery);
          this.setDeceasedId(deceased);
          this.setDecedent(deceased);
          this.setDeceasedYearsLife(deceased);
          this.setLocation(deceased);
        }
      });
    }

    if (NO_SLUG) {
      void this.loadCemeteries();
    } else {
      redirectToMain();
    }
  }

  isLoading: boolean = false;
  error: string = '';
  slug: string = '';
  cartString: string = '';
  cemeteries: ICemeteryRecord[] = [];
  deceasedId: string = '';
  decedent = {
    name: '',
    link: '#',
  };
  cemeteryName: string = '';
  deceasedYearsLife: string = '';
  location: string = '';

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.init();
  }

  setCemeteries(cemeteries: ICemeteryRecord[]) {
    this.cemeteries = cemeteries;
  }

  setLoadingState(value: boolean) {
    this.isLoading = value;
  }

  setError(error: string) {
    this.error = error;
  }

  setSlug(slug: string) {
    this.slug = slug;
  }

  setCartString(cartString: string) {
    this.cartString = cartString === '{}' ? '' : cartString;
  }

  setDeceasedId(deceased: IDeceased) {
    this.deceasedId = deceased.id;
  }

  setCemeteryName(cemeteryName: string) {
    this.cemeteryName = cemeteryName;
  }

  setDecedent(person: IPerson) {
    this.decedent = {
      name: getPersonName(person),
      link: `${CNAME}/${this.slug}`,
    };
  }

  setDeceasedYearsLife(deceased: IDeceased) {
    this.deceasedYearsLife = getDeceasedYearsLife(deceased);
  }

  setLocation(deceased: IDeceased) {
    this.location = getGraveLocation(deceased);
  }

  get cemeteriesOptions(): IOption[] {
    return this.cemeteries.map(({ id, name }) => ({ id, value: name }));
  }

  get isConfigAndThemeLoaded() {
    return this.isConfigCorrect && this.isLoadedTheme;
  }

  get isConfigCorrect() {
    return this.missingConfigVars.length === 0;
  }

  get isLoadedTheme() {
    return !!window.getComputedStyle(document.body).getPropertyValue(THEME_REQUIRED_PROPERTY);
  }

  get isNoSlug() {
    return !this.deceasedId;
  }

  get missingConfigVars() {
    return CONFIG_REQUIRED_PARAMS_MAPPED.filter((item) => !item.value);
  }

  get queryParams() {
    return Object.entries(filterEmptyObjectProperties({ slug: this.slug, cart: this.cartString }))
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
  }

  async loadCemeteries() {
    this.setLoadingState(true);

    try {
      const result = await doGet<ICemeteryRecord[]>(ApiRoute.CEMETERIES_EXTENDED);

      if (result.status === ApiResponseStatus.SUCCESS) {
        this.setCemeteries(result.data ?? []);
      }
    } catch (e) {
      console.log('e', e);
    }

    this.setLoadingState(false);
  }

  async loadDeceasedData(slug: string) {
    this.setLoadingState(true);

    try {
      const { data, status } = await doGet(`deceaseds/${slug}`);

      if (status === ApiResponseStatus.SUCCESS) {
        this.setLoadingState(false);
        return mapApiToDeceased(data);
      }
    } catch (e) {
      this.setLoadingState(false);
      console.log('e', e);
      const error = e as AxiosError;
      if (error.response?.status === ApiResponseStatus.NOT_FOUND) {
        redirectToMain();
      } else if (e instanceof Error) {
        this.setError(e.message);
      } else {
        this.setError(ErrorText.DEFAULT);
      }
    }
  }
}

export default new GlobalStore();
